import { ChevronDown } from 'lucide-react';

export type DriverDetailsData = {
  firstName: string;
  lastName: string;
  driverId: string;
  gender: string;
  languages: string;
  mobileNumber: string;
  emailAddress: string;
};

type Step1Props = {
  data: DriverDetailsData;
  updateData: (data: Partial<DriverDetailsData>) => void;
};

export const Step1PersonalInformation = ({ data, updateData }: Step1Props) => {
  return (
    <div className="flex flex-col gap-8 w-full max-w-[1000px] mx-auto px-[32px]">
      
      {/* Personal Information Section */}
      <div className="flex flex-col gap-4">
        <h3 className="font-host text-[16px] font-bold text-[#1A1A1A]">Personal Information</h3>
        <div className="w-full rounded-[16px] border border-[#E0E0E0] bg-white p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
          
          {/* First Name */}
          <div className="flex flex-col gap-1.5">
            <label className="font-host text-[13px] font-medium text-[#757575]">First Name</label>
            <input
              type="text"
              placeholder="Enter first name"
              value={data.firstName}
              onChange={(e) => updateData({ firstName: e.target.value })}
              className="w-full h-[48px] rounded-[8px] border border-[#E0E0E0] bg-white px-4 font-host text-[14px] text-[#1A1A1A] placeholder:text-[#9E9E9E] focus:outline-none focus:border-[#FF6A1A]"
            />
          </div>

          {/* Last Name */}
          <div className="flex flex-col gap-1.5">
            <label className="font-host text-[13px] font-medium text-[#757575]">Last Name</label>
            <input
              type="text"
              placeholder="Enter Last name"
              value={data.lastName}
              onChange={(e) => updateData({ lastName: e.target.value })}
              className="w-full h-[48px] rounded-[8px] border border-[#E0E0E0] bg-white px-4 font-host text-[14px] text-[#1A1A1A] placeholder:text-[#9E9E9E] focus:outline-none focus:border-[#FF6A1A]"
            />
          </div>

          {/* Driver ID */}
          <div className="flex flex-col gap-1.5">
            <label className="font-host text-[13px] font-medium text-[#757575]">Driver ID</label>
            <div className="relative">
              <input
                type="text"
                value={data.driverId || 'Auto-generated'}
                readOnly
                className={`w-full h-[48px] rounded-[8px] border border-[#E0E0E0] bg-white px-4 font-host text-[14px] focus:outline-none ${
                  !data.driverId ? 'text-[#9E9E9E]' : 'text-[#1A1A1A]'
                }`}
              />
              <div className="absolute right-3 top-1/2 -translate-y-1/2 px-3 py-1 bg-[#A8CD76] text-white rounded-[16px] font-host text-[12px] font-medium">
                Auto
              </div>
            </div>
          </div>

          {/* Gender */}
          <div className="flex flex-col gap-1.5">
            <label className="font-host text-[13px] font-medium text-[#757575]">Gender</label>
            <div className="relative">
              <select
                value={data.gender}
                onChange={(e) => updateData({ gender: e.target.value })}
                className={`w-full h-[48px] rounded-[8px] border border-[#E0E0E0] bg-white px-4 font-host text-[14px] appearance-none focus:outline-none focus:border-[#FF6A1A] cursor-pointer ${
                  !data.gender ? 'text-[#9E9E9E]' : 'text-[#1A1A1A]'
                }`}
              >
                <option value="" disabled className="text-[#9E9E9E]">Select Gender</option>
                <option value="Male" className="text-[#1A1A1A]">Male</option>
                <option value="Female" className="text-[#1A1A1A]">Female</option>
                <option value="Other" className="text-[#1A1A1A]">Other</option>
              </select>
              <ChevronDown
                className="absolute right-4 top-1/2 -translate-y-1/2 text-[#1A1A1A] pointer-events-none"
                size={20}
              />
            </div>
          </div>

          {/* Languages */}
          <div className="flex flex-col gap-1.5">
            <label className="font-host text-[13px] font-medium text-[#757575]">Languages</label>
            <input
              type="text"
              placeholder="Enter Languages"
              value={data.languages}
              onChange={(e) => updateData({ languages: e.target.value })}
              className="w-full h-[48px] rounded-[8px] border border-[#E0E0E0] bg-white px-4 font-host text-[14px] text-[#1A1A1A] placeholder:text-[#9E9E9E] focus:outline-none focus:border-[#FF6A1A]"
            />
          </div>

        </div>
      </div>

      {/* Contact Information Section */}
      <div className="flex flex-col gap-4">
        <h3 className="font-host text-[16px] font-bold text-[#1A1A1A]">Contact Information</h3>
        <div className="w-full rounded-[16px] border border-[#E0E0E0] bg-white p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
          
          {/* Mobile Number */}
          <div className="flex flex-col gap-1.5">
            <label className="font-host text-[13px] font-medium text-[#757575]">Mobile Number</label>
            <input
              type="tel"
              placeholder="+449876543456"
              value={data.mobileNumber}
              onChange={(e) => updateData({ mobileNumber: e.target.value })}
              className="w-full h-[48px] rounded-[8px] border border-[#E0E0E0] bg-white px-4 font-host text-[14px] text-[#1A1A1A] placeholder:text-[#9E9E9E] focus:outline-none focus:border-[#FF6A1A]"
            />
          </div>

          {/* Email Address */}
          <div className="flex flex-col gap-1.5">
            <label className="font-host text-[13px] font-medium text-[#757575]">Email Address</label>
            <input
              type="email"
              placeholder="Admin@1gmail.com"
              value={data.emailAddress}
              onChange={(e) => updateData({ emailAddress: e.target.value })}
              className="w-full h-[48px] rounded-[8px] border border-[#E0E0E0] bg-white px-4 font-host text-[14px] text-[#1A1A1A] placeholder:text-[#9E9E9E] focus:outline-none focus:border-[#FF6A1A]"
            />
          </div>

        </div>
      </div>

    </div>
  );
};